// Touch gestures: MDN Touch Events + Math.hypot
// https://developer.mozilla.org/en-US/docs/Web/API/Touch_events

import {CSSProperties, useCallback, useEffect, useRef, useState} from "react";

import {GESTURE_SWIPE_DEADBAND} from "@pg-design/slider-gallery-module";

import {
    clampPinchTranslate,
    DOUBLE_TAP_ZOOM_SCALE,
    getPinchDistance,
    getPinchZoomImageStyle,
    IDoubleTapState,
    isDoubleTap,
    MAX_PINCH_ZOOM_SCALE,
    MIN_PINCH_ZOOM_SCALE,
    shouldSnapBackScale
} from "../utils/pinch_zoom_utils";

interface ITransform {
    scale: number;
    translateX: number;
    translateY: number;
}

interface IPinchGesture {
    initialDistance: number;
    baseScale: number;
}

interface IPanGesture {
    startX: number;
    startY: number;
    baseTranslateX: number;
    baseTranslateY: number;
}

interface IPointerGesture {
    startX: number;
    startY: number;
    blockVertical: boolean;
}

// panning: touch handler branch; gesturing: disables CSS transition during live pinch/pan
interface IGestureFlags {
    panning: boolean;
    gesturing: boolean;
}

export interface IUsePinchZoomResult {
    isZoomed: boolean;
    containerRef: (node: HTMLDivElement | null) => void;
    getImageStyle: () => CSSProperties;
    reset: () => void;
}

const createInitialTransform = (): ITransform => ({
    scale: MIN_PINCH_ZOOM_SCALE,
    translateX: 0,
    translateY: 0
});

export const usePinchZoom = (enabled: boolean): IUsePinchZoomResult => {
    // Callback ref: sync ref for handlers + state node to re-attach listeners on mount/unmount.
    const containerElementRef = useRef<HTMLDivElement | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        containerElementRef.current = node;
        setContainerEl(node);
    }, []);

    const transformRef = useRef<ITransform>(createInitialTransform());
    const [transform, setTransform] = useState<ITransform>(createInitialTransform);

    const pinchRef = useRef<IPinchGesture>({initialDistance: 0, baseScale: MIN_PINCH_ZOOM_SCALE});
    const panRef = useRef<IPanGesture>({startX: 0, startY: 0, baseTranslateX: 0, baseTranslateY: 0});
    const gestureRef = useRef<IGestureFlags>({panning: false, gesturing: false});
    const doubleTapRef = useRef<IDoubleTapState>({lastTapTime: 0, lastTapX: 0, lastTapY: 0});
    const pointerRef = useRef<IPointerGesture>({startX: 0, startY: 0, blockVertical: false});

    const applyTransform = useCallback((newScale: number, newX: number, newY: number) => {
        const next = {scale: newScale, translateX: newX, translateY: newY};
        transformRef.current = next;
        setTransform(next);
    }, []);

    const reset = useCallback(() => {
        applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
    }, [applyTransform]);

    const endTouchGesture = () => {
        gestureRef.current = {panning: false, gesturing: false};
        pinchRef.current.initialDistance = 0;
    };

    useEffect(() => {
        if (!enabled) reset();
    }, [enabled, reset]);

    const clampTranslate = useCallback((newScale: number, newX: number, newY: number): [number, number] => {
        const el = containerElementRef.current;
        if (!el) return [newX, newY];

        return clampPinchTranslate({containerWidth: el.clientWidth, containerHeight: el.clientHeight, scale: newScale, x: newX, y: newY});
    }, []);

    const getTouchesPinchDistance = useCallback((touches: TouchList) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;

        return getPinchDistance(dx, dy);
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const el = containerEl;
        if (!el) return;

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const initialDistance = getTouchesPinchDistance(e.touches);
                if (initialDistance === 0) return;

                pinchRef.current = {initialDistance, baseScale: transformRef.current.scale};
                gestureRef.current = {panning: false, gesturing: true};
            } else if (e.touches.length === 1 && transformRef.current.scale > MIN_PINCH_ZOOM_SCALE) {
                panRef.current = {
                    startX: e.touches[0].clientX,
                    startY: e.touches[0].clientY,
                    baseTranslateX: transformRef.current.translateX,
                    baseTranslateY: transformRef.current.translateY
                };
                gestureRef.current = {panning: true, gesturing: true};
            } else if (e.touches.length === 1) {
                gestureRef.current.panning = false;
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                if (!pinchRef.current.initialDistance) return;

                const newDistance = getTouchesPinchDistance(e.touches);
                const rawScale = pinchRef.current.baseScale * (newDistance / pinchRef.current.initialDistance);
                const clampedScale = Math.max(MIN_PINCH_ZOOM_SCALE, Math.min(MAX_PINCH_ZOOM_SCALE, rawScale));
                const {translateX, translateY} = transformRef.current;
                const [clampedX, clampedY] = clampTranslate(clampedScale, translateX, translateY);
                applyTransform(clampedScale, clampedX, clampedY);
            } else if (e.touches.length === 1 && gestureRef.current.panning) {
                e.preventDefault();
                const {startX, startY, baseTranslateX, baseTranslateY} = panRef.current;
                const dx = e.touches[0].clientX - startX;
                const dy = e.touches[0].clientY - startY;
                const {scale} = transformRef.current;
                const [clampedX, clampedY] = clampTranslate(scale, baseTranslateX + dx, baseTranslateY + dy);
                applyTransform(scale, clampedX, clampedY);
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            // Snap back near 1× only after pinch-out — not at 1× (would block double-tap zoom-in).
            if (e.touches.length === 0 && shouldSnapBackScale({scale: transformRef.current.scale})) {
                endTouchGesture();
                applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
                return;
            }

            if (e.touches.length === 0) endTouchGesture();

            // Double-tap: two taps within time/distance limits toggle zoom.
            if (e.changedTouches.length === 1) {
                const now = Date.now();
                const tapX = e.changedTouches[0].clientX;
                const tapY = e.changedTouches[0].clientY;

                if (isDoubleTap({now, tapX, tapY, state: doubleTapRef.current})) {
                    if (transformRef.current.scale > MIN_PINCH_ZOOM_SCALE) {
                        applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
                    } else {
                        const [clampedX, clampedY] = clampTranslate(DOUBLE_TAP_ZOOM_SCALE, 0, 0);
                        applyTransform(DOUBLE_TAP_ZOOM_SCALE, clampedX, clampedY);
                    }
                    doubleTapRef.current = {lastTapTime: 0, lastTapX: 0, lastTapY: 0};
                } else {
                    doubleTapRef.current = {lastTapTime: now, lastTapX: tapX, lastTapY: tapY};
                }
            }
        };

        const handleTouchCancel = () => {
            endTouchGesture();
            applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
        };

        const handlePointerCapture = (e: PointerEvent) => {
            handleViewerPointerCapture(e, transformRef.current.scale, pointerRef.current, GESTURE_SWIPE_DEADBAND.DEFAULT);
        };

        el.addEventListener("touchstart", handleTouchStart, {passive: false});
        el.addEventListener("touchmove", handleTouchMove, {passive: false});
        el.addEventListener("touchend", handleTouchEnd, {passive: false});
        el.addEventListener("touchcancel", handleTouchCancel);
        el.addEventListener("pointerdown", handlePointerCapture, true);
        el.addEventListener("pointermove", handlePointerCapture, true);

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchCancel);
            el.removeEventListener("pointerdown", handlePointerCapture, true);
            el.removeEventListener("pointermove", handlePointerCapture, true);
        };
    }, [enabled, containerEl, applyTransform, clampTranslate, getTouchesPinchDistance]);

    const getImageStyle = useCallback(
        (): CSSProperties =>
            getPinchZoomImageStyle({
                scale: transform.scale,
                translateX: transform.translateX,
                translateY: transform.translateY,
                isGesturing: gestureRef.current.gesturing
            }),
        [transform]
    );

    return {
        isZoomed: transform.scale > MIN_PINCH_ZOOM_SCALE,
        containerRef,
        getImageStyle,
        reset
    };
};

// Capture phase: block slider pointer events while zoomed, or on vertical swipes at 1×.
// pointerup/cancel must pass through so SliderGallery clears active pointer tracking.
function handleViewerPointerCapture(e: PointerEvent, scale: number, pointer: IPointerGesture, deadband: number) {
    if (scale > MIN_PINCH_ZOOM_SCALE) {
        if (e.type !== "pointerup" && e.type !== "pointercancel") e.stopPropagation();
        return;
    }

    if (e.type === "pointerdown") {
        pointer.startX = e.clientX;
        pointer.startY = e.clientY;
        pointer.blockVertical = false;
        return;
    }

    if (e.type === "pointermove") {
        const dx = Math.abs(e.clientX - pointer.startX);
        const dy = Math.abs(e.clientY - pointer.startY);

        if (dy > deadband && dy > dx) pointer.blockVertical = true;
        if (pointer.blockVertical) e.stopPropagation();
    }
}
// Touch gestures: MDN Touch Events + Math.hypot
// https://developer.mozilla.org/en-US/docs/Web/API/Touch_events

import {CSSProperties, useCallback, useEffect, useRef, useState} from "react";

import {GESTURE_SWIPE_DEADBAND} from "@pg-design/slider-gallery-module";

import {
    clampPinchTranslate,
    DOUBLE_TAP_ZOOM_SCALE,
    getPinchDistance,
    getPinchZoomImageStyle,
    IDoubleTapState,
    isDoubleTap,
    MAX_PINCH_ZOOM_SCALE,
    MIN_PINCH_ZOOM_SCALE,
    shouldSnapBackScale
} from "../utils/pinch_zoom_utils";

interface ITransform {
    scale: number;
    translateX: number;
    translateY: number;
}

interface IPinchGesture {
    initialDistance: number;
    baseScale: number;
}

interface IPanGesture {
    startX: number;
    startY: number;
    baseTranslateX: number;
    baseTranslateY: number;
}

interface IPointerGesture {
    startX: number;
    startY: number;
    blockVertical: boolean;
}

// panning: touch handler branch; gesturing: disables CSS transition during live pinch/pan
interface IGestureFlags {
    panning: boolean;
    gesturing: boolean;
}

export interface IUsePinchZoomResult {
    isZoomed: boolean;
    containerRef: (node: HTMLDivElement | null) => void;
    getImageStyle: () => CSSProperties;
    reset: () => void;
}

const createInitialTransform = (): ITransform => ({
    scale: MIN_PINCH_ZOOM_SCALE,
    translateX: 0,
    translateY: 0
});

export const usePinchZoom = (enabled: boolean): IUsePinchZoomResult => {
    // Callback ref: sync ref for handlers + state node to re-attach listeners on mount/unmount.
    const containerElementRef = useRef<HTMLDivElement | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        containerElementRef.current = node;
        setContainerEl(node);
    }, []);

    const transformRef = useRef<ITransform>(createInitialTransform());
    const [transform, setTransform] = useState<ITransform>(createInitialTransform);

    const pinchRef = useRef<IPinchGesture>({initialDistance: 0, baseScale: MIN_PINCH_ZOOM_SCALE});
    const panRef = useRef<IPanGesture>({startX: 0, startY: 0, baseTranslateX: 0, baseTranslateY: 0});
    const gestureRef = useRef<IGestureFlags>({panning: false, gesturing: false});
    const doubleTapRef = useRef<IDoubleTapState>({lastTapTime: 0, lastTapX: 0, lastTapY: 0});
    const pointerRef = useRef<IPointerGesture>({startX: 0, startY: 0, blockVertical: false});

    const applyTransform = useCallback((newScale: number, newX: number, newY: number) => {
        const next = {scale: newScale, translateX: newX, translateY: newY};
        transformRef.current = next;
        setTransform(next);
    }, []);

    const reset = useCallback(() => {
        applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
    }, [applyTransform]);

    const endTouchGesture = () => {
        gestureRef.current = {panning: false, gesturing: false};
        pinchRef.current.initialDistance = 0;
    };

    useEffect(() => {
        if (!enabled) reset();
    }, [enabled, reset]);

    const clampTranslate = useCallback((newScale: number, newX: number, newY: number): [number, number] => {
        const el = containerElementRef.current;
        if (!el) return [newX, newY];

        return clampPinchTranslate({containerWidth: el.clientWidth, containerHeight: el.clientHeight, scale: newScale, x: newX, y: newY});
    }, []);

    const getTouchesPinchDistance = useCallback((touches: TouchList) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;

        return getPinchDistance(dx, dy);
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const el = containerEl;
        if (!el) return;

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const initialDistance = getTouchesPinchDistance(e.touches);
                if (initialDistance === 0) return;

                pinchRef.current = {initialDistance, baseScale: transformRef.current.scale};
                gestureRef.current = {panning: false, gesturing: true};
            } else if (e.touches.length === 1 && transformRef.current.scale > MIN_PINCH_ZOOM_SCALE) {
                panRef.current = {
                    startX: e.touches[0].clientX,
                    startY: e.touches[0].clientY,
                    baseTranslateX: transformRef.current.translateX,
                    baseTranslateY: transformRef.current.translateY
                };
                gestureRef.current = {panning: true, gesturing: true};
            } else if (e.touches.length === 1) {
                gestureRef.current.panning = false;
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                if (!pinchRef.current.initialDistance) return;

                const newDistance = getTouchesPinchDistance(e.touches);
                const rawScale = pinchRef.current.baseScale * (newDistance / pinchRef.current.initialDistance);
                const clampedScale = Math.max(MIN_PINCH_ZOOM_SCALE, Math.min(MAX_PINCH_ZOOM_SCALE, rawScale));
                const {translateX, translateY} = transformRef.current;
                const [clampedX, clampedY] = clampTranslate(clampedScale, translateX, translateY);
                applyTransform(clampedScale, clampedX, clampedY);
            } else if (e.touches.length === 1 && gestureRef.current.panning) {
                e.preventDefault();
                const {startX, startY, baseTranslateX, baseTranslateY} = panRef.current;
                const dx = e.touches[0].clientX - startX;
                const dy = e.touches[0].clientY - startY;
                const {scale} = transformRef.current;
                const [clampedX, clampedY] = clampTranslate(scale, baseTranslateX + dx, baseTranslateY + dy);
                applyTransform(scale, clampedX, clampedY);
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            // Snap back near 1× only after pinch-out — not at 1× (would block double-tap zoom-in).
            if (e.touches.length === 0 && shouldSnapBackScale({scale: transformRef.current.scale})) {
                endTouchGesture();
                applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
                return;
            }

            if (e.touches.length === 0) endTouchGesture();

            // Double-tap: two taps within time/distance limits toggle zoom.
            if (e.changedTouches.length === 1) {
                const now = Date.now();
                const tapX = e.changedTouches[0].clientX;
                const tapY = e.changedTouches[0].clientY;

                if (isDoubleTap({now, tapX, tapY, state: doubleTapRef.current})) {
                    if (transformRef.current.scale > MIN_PINCH_ZOOM_SCALE) {
                        applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
                    } else {
                        const [clampedX, clampedY] = clampTranslate(DOUBLE_TAP_ZOOM_SCALE, 0, 0);
                        applyTransform(DOUBLE_TAP_ZOOM_SCALE, clampedX, clampedY);
                    }
                    doubleTapRef.current = {lastTapTime: 0, lastTapX: 0, lastTapY: 0};
                } else {
                    doubleTapRef.current = {lastTapTime: now, lastTapX: tapX, lastTapY: tapY};
                }
            }
        };

        const handleTouchCancel = () => {
            endTouchGesture();
            applyTransform(MIN_PINCH_ZOOM_SCALE, 0, 0);
        };

        const handlePointerCapture = (e: PointerEvent) => {
            handleViewerPointerCapture(e, transformRef.current.scale, pointerRef.current, GESTURE_SWIPE_DEADBAND.DEFAULT);
        };

        el.addEventListener("touchstart", handleTouchStart, {passive: false});
        el.addEventListener("touchmove", handleTouchMove, {passive: false});
        el.addEventListener("touchend", handleTouchEnd, {passive: false});
        el.addEventListener("touchcancel", handleTouchCancel);
        el.addEventListener("pointerdown", handlePointerCapture, true);
        el.addEventListener("pointermove", handlePointerCapture, true);

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchCancel);
            el.removeEventListener("pointerdown", handlePointerCapture, true);
            el.removeEventListener("pointermove", handlePointerCapture, true);
        };
    }, [enabled, containerEl, applyTransform, clampTranslate, getTouchesPinchDistance]);

    const getImageStyle = useCallback(
        (): CSSProperties =>
            getPinchZoomImageStyle({
                scale: transform.scale,
                translateX: transform.translateX,
                translateY: transform.translateY,
                isGesturing: gestureRef.current.gesturing
            }),
        [transform]
    );

    return {
        isZoomed: transform.scale > MIN_PINCH_ZOOM_SCALE,
        containerRef,
        getImageStyle,
        reset
    };
};

// Capture phase: block slider pointer events while zoomed, or on vertical swipes at 1×.
// pointerup/cancel must pass through so SliderGallery clears active pointer tracking.
function handleViewerPointerCapture(e: PointerEvent, scale: number, pointer: IPointerGesture, deadband: number) {
    if (scale > MIN_PINCH_ZOOM_SCALE) {
        if (e.type !== "pointerup" && e.type !== "pointercancel") e.stopPropagation();
        return;
    }

    if (e.type === "pointerdown") {
        pointer.startX = e.clientX;
        pointer.startY = e.clientY;
        pointer.blockVertical = false;
        return;
    }

    if (e.type === "pointermove") {
        const dx = Math.abs(e.clientX - pointer.startX);
        const dy = Math.abs(e.clientY - pointer.startY);

        if (dy > deadband && dy > dx) pointer.blockVertical = true;
        if (pointer.blockVertical) e.stopPropagation();
    }
}
